import { useState } from 'react'
import { ADMIN_BOOKING_FEEDBACK } from '../../lib/admin-booking-actions'
import { cancelAdminBooking, deleteTestAdminBooking } from '../../lib/admin-command'
import { type AdminCanonicalBooking } from '../../lib/admin-workspace'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'

export type AdminDestructiveAction = 'delete' | 'cancel'

export function AdminBookingConfirmDialog({
  booking,
  action,
  onDismiss,
  onDeleted,
  onCancelled,
  onMessage,
}: Readonly<{
  booking: AdminCanonicalBooking | null
  action: AdminDestructiveAction | null
  onDismiss: () => void
  onDeleted?: (bookingId: string) => void
  onCancelled?: (booking: AdminCanonicalBooking) => void
  onMessage: (message: string | null, error?: boolean) => void
}>) {
  const [busy, setBusy] = useState(false)
  const open = Boolean(booking && action)
  const depositPaid = booking?.payment_status === 'paid' || booking?.payment_status === 'partially_refunded'

  async function confirm() {
    if (!booking || !action) return
    setBusy(true)
    onMessage(null)
    try {
      if (action === 'delete') {
        await deleteTestAdminBooking(booking.id)
        onMessage(ADMIN_BOOKING_FEEDBACK.deleted)
        onDeleted?.(booking.id)
        onDismiss()
        return
      }
      const result = await cancelAdminBooking(booking.id)
      onMessage(typeof result.warning === 'string' && result.warning
        ? result.warning
        : ADMIN_BOOKING_FEEDBACK.cancelled)
      onCancelled?.(booking)
      onDismiss()
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'The request failed.'
      onMessage(
        action === 'delete'
          ? ADMIN_BOOKING_FEEDBACK.deleteFailed(reason)
          : ADMIN_BOOKING_FEEDBACK.cancelFailed(reason),
        true,
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !busy) onDismiss()
      }}
    >
      <AlertDialogContent
        onCloseAutoFocus={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {action === 'delete' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete TEST booking?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{booking?.public_reference}</strong>
                <span>
                  This permanently removes this TEST booking and its associated test-only booking data.
                  This cannot be undone.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy} type="button">Cancel</AlertDialogCancel>
              <button
                className="admin-alert-destructive"
                disabled={busy}
                onClick={(event) => {
                  event.preventDefault()
                  void confirm()
                }}
                type="button"
              >
                Delete TEST booking
              </button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel booking?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{booking?.public_reference}</strong>
                <span>The appointment will be cancelled and the slot will be released.</span>
                {depositPaid ? (
                  <span>The A$100 deposit has been paid and is not automatically refunded.</span>
                ) : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy} type="button">Keep booking</AlertDialogCancel>
              <button
                className="admin-alert-destructive"
                disabled={busy}
                onClick={(event) => {
                  event.preventDefault()
                  void confirm()
                }}
                type="button"
              >
                Cancel booking
              </button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}
